import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoEyeOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoPrintOutline,
  IoCheckmarkOutline,
  IoCloseOutline,
} from "react-icons/io5";
import arService from "../../../../../services/arService";
import { hasPermission } from "../../../../../utils/permission";
import "./ReceiptList.css";
import { showMessageBox } from '../../../../site/fn';
import { EmptyState, ErrorState } from '../../../../../shared-ui/States';

const ReceiptList = () => {
  const [receipts, setReceipts] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const canEdit = hasPermission("ar-receipt", "set");
  const canDelete = hasPermission("ar-receipt", "delete");
  const canApprove = hasPermission("ar-receipt", "approve");

  const isPendingApprovalStatus = (status) =>
    status === "Pending Approval" || status === "Pending";

  const getStatusBadge = (approvalStatus, isDraft) => {
    if (isDraft || approvalStatus === "Draft") {
      return <span className="status-badge draft">Draft</span>;
    }
    if (approvalStatus === "Approved") {
      return <span className="status-badge approved">Approved</span>;
    }
    if (approvalStatus === "Rejected") {
      return <span className="status-badge rejected">Rejected</span>;
    }
    if (isPendingApprovalStatus(approvalStatus)) {
      return <span className="status-badge pending">Pending Approval</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const isEditableReceipt = (receipt) =>
    receipt.is_draft ||
    receipt.approval_status === "Draft" ||
    receipt.approval_status === "Rejected" ||
    (receipt.approval_status === "Approved" && receipt.pending_allocation);

  const canDeleteDocument = (doc) =>
    doc.approval_status !== "Approved" && !doc.is_deleted;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0.00";
    return `₹ ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getPaymentModeBadge = (mode) => {
    const badges = {
      Cash: <span className="payment-badge cash">Cash</span>,
      Cheque: <span className="payment-badge cheque">Cheque</span>,
      "Bank Transfer": <span className="payment-badge bank">Bank Transfer</span>,
      UPI: <span className="payment-badge upi">UPI</span>,
      "Credit Card": <span className="payment-badge card">Credit Card</span>,
      "Debit Card": <span className="payment-badge card">Debit Card</span>,
    };
    return badges[mode] || <span className="payment-badge other">{mode}</span>;
  };

  useEffect(() => {
    fetchReceipts();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(receipts);
    } else {
      const filtered = receipts.filter((receipt) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (receipt.receipt_no && receipt.receipt_no.toLowerCase().includes(searchLower)) ||
          (receipt.customer_name && receipt.customer_name.toLowerCase().includes(searchLower)) ||
          (receipt.payment_mode && receipt.payment_mode.toLowerCase().includes(searchLower)) ||
          (receipt.reference_no && receipt.reference_no.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, receipts]);

  const fetchReceipts = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const data = await arService.getReceipts();
      const sorted = [...data].sort((a, b) => {
        const idDiff = Number(b.id || 0) - Number(a.id || 0);
        if (idDiff !== 0) return idDiff;
        return new Date(b.receipt_date || 0) - new Date(a.receipt_date || 0);
      });
      setReceipts(sorted);
      setFilteredData(sorted);
    } catch (error) {
      console.error("Error fetching receipts:", error);
      setLoadError(error.message || "Failed to fetch receipts.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (id) => {
    navigate(`/ar/receipts/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/ar/receipts/${id}/edit`);
  };

  const handlePrint = (id) => {
    navigate(`/ar/receipts/${id}/print`);
  };

  const handleApprove = async (id, receiptNo) => {
    if (!window.confirm(`Are you sure you want to approve Receipt ${receiptNo}?`)) {
      return;
    }

    try {
      await arService.approveReceipt(id);
      showMessageBox("Receipt approved successfully!", "success");
      fetchReceipts();
    } catch (error) {
      console.error("Error approving receipt:", error);
      showMessageBox(error.message || "Failed to approve receipt. Please try again.", "error");
    }
  };

  const handleReject = async (id, receiptNo) => {
    const reason = window.prompt(`Enter reason for rejecting Receipt ${receiptNo}:`);
    if (!reason || reason.trim() === "") {
      return;
    }

    try {
      await arService.rejectReceipt(id, reason.trim());
      showMessageBox("Receipt rejected successfully!", "success");
      fetchReceipts();
    } catch (error) {
      console.error("Error rejecting receipt:", error);
      showMessageBox(error.message || "Failed to reject receipt. Please try again.", "error");
    }
  };

  const handleDelete = async (id, receiptNo) => {
    const reason = window.prompt(
      `⚠️ Delete Receipt ${receiptNo}\n\n` +
      `This action will permanently block this document number (${receiptNo}).\n` +
      `The number will never be reused.\n\n` +
      `Please provide a reason for deletion:`
    );
    
    if (reason === null) {
      // User clicked cancel
      return;
    }
    
    if (!reason || reason.trim() === "") {
      showMessageBox("Deletion reason is required. Receipt was not deleted.", "error");
      return;
    }

    if (!window.confirm(
      `Are you sure you want to delete Receipt ${receiptNo}?\n\n` +
      `Reason: ${reason}\n\n` +
      `This action cannot be undone.`
    )) {
      return;
    }

    try {
      await arService.deleteReceipt(id, reason.trim());
      showMessageBox("Receipt deleted successfully!", "success");
      fetchReceipts();
    } catch (error) {
      console.error("Error deleting receipt:", error);
      showMessageBox("Failed to delete receipt. Please try again.", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading receipts...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="receipt-list-container">
        <ErrorState
          title="Could not load receipts"
          description={loadError}
          onRetry={fetchReceipts}
        />
      </div>
    );
  }

  return (
    <div className="receipt-list-container">
      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Receipt No, Customer, Payment Mode, or Reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="list-info">
          <span>Total: {filteredData.length} receipts</span>
        </div>
      </div>

      <div className="table-container">
        {filteredData.length === 0 ? (
          <EmptyState
            title="No receipts"
            description={
              searchTerm
                ? "No receipts found matching your search."
                : "Receipts will appear here once they are recorded."
            }
          />
        ) : (
        <table className="receipt-table">
          <thead>
            <tr>
              <th>Receipt No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Payment Mode</th>
              <th>Reference No</th>
              <th className="text-right">Amount</th>
              <th>Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
              {filteredData.map((receipt) => (
                <tr key={receipt.id}>
                  <td>
                    <strong>{receipt.receipt_no || "N/A"}</strong>
                  </td>
                  <td>{formatDate(receipt.receipt_date)}</td>
                  <td>{receipt.customer_name || "N/A"}</td>
                  <td>{getPaymentModeBadge(receipt.payment_mode)}</td>
                  <td>{receipt.reference_no || "N/A"}</td>
                  <td className="text-right">{formatCurrency(receipt.amount)}</td>
                  <td>{getStatusBadge(receipt.approval_status, receipt.is_draft)}</td>
                  <td className="text-center">
                    <div className="action-buttons">
                      <button
                        type="button"
                        className="btn-action btn-view"
                        onClick={() => handleView(receipt.id)}
                        title="View Details"
                      >
                        <IoEyeOutline size={16} />
                      </button>
                      {canEdit && isEditableReceipt(receipt) && (
                        <button
                          type="button"
                          className="btn-action btn-edit"
                          onClick={() => handleEdit(receipt.id)}
                          title="Edit"
                        >
                          <IoCreateOutline size={16} />
                        </button>
                      )}
                      {canApprove && isPendingApprovalStatus(receipt.approval_status) && (
                        <>
                          <button
                            type="button"
                            className="btn-action btn-approve"
                            onClick={() => handleApprove(receipt.id, receipt.receipt_no)}
                            title="Approve"
                          >
                            <IoCheckmarkOutline size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn-action btn-reject"
                            onClick={() => handleReject(receipt.id, receipt.receipt_no)}
                            title="Reject"
                          >
                            <IoCloseOutline size={16} />
                          </button>
                        </>
                      )}
                      {receipt.approval_status === "Approved" && (
                        <button
                          type="button"
                          className="btn-action btn-print"
                          onClick={() => handlePrint(receipt.id)}
                          title="Print PDF"
                        >
                          <IoPrintOutline size={16} />
                        </button>
                      )}
                      {canDelete && canDeleteDocument(receipt) && (
                        <button
                          type="button"
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(receipt.id, receipt.receipt_no)}
                          title="Delete"
                        >
                          <IoTrashOutline size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
};

export default ReceiptList;
